import { useCallback, useEffect, useState } from "react";
import { FileDown, Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { adminApi, errMsg } from "@/lib/api";
import { fmtDT } from "@/pages/admin/AdminAccounts";

export async function downloadManual() {
  try {
    const { data } = await adminApi.get("/manual", { responseType: "blob" });
    const url = URL.createObjectURL(data);
    const a = document.createElement("a");
    a.href = url;
    a.download = "smart-in-retail-user-manual.pdf";
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 2000);
  } catch (e) {
    toast.error(e?.response?.status === 404 ? "Manual has not been generated yet" : errMsg(e));
  }
}

export function ManualButton() {
  const [busy, setBusy] = useState(false);
  return (
    <button
      onClick={async () => { setBusy(true); await downloadManual(); setBusy(false); }}
      data-testid="admin-manual-download"
      className="hidden h-10 items-center gap-2 rounded-lg bg-amber-500 px-3 text-sm font-bold text-slate-950 hover:bg-amber-400 sm:flex"
    >
      {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileDown className="h-4 w-4" />} User manual (PDF)
    </button>
  );
}

export default function AdminManual() {
  const [st, setSt] = useState(null);
  const load = useCallback(() => adminApi.get("/manual/status").then((r) => setSt(r.data)).catch((e) => toast.error(errMsg(e))), []);
  useEffect(() => { load(); }, [load]);
  const running = st?.job?.state === "running";
  const bstate = st?.browser?.state;
  const preparing = bstate === "checking" || bstate === "installing";
  useEffect(() => {
    if (!running && !preparing) return;
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [running, preparing, load]);

  const regenerate = async () => {
    try {
      const { data } = await adminApi.post("/manual/regenerate");
      setSt((s) => ({ ...s, job: data.job }));
      toast.success("Regenerating: capturing fresh screenshots (about 2–4 minutes)");
    } catch (e) {
      toast.error(errMsg(e));
      load();
    }
  };

  return (
    <div data-testid="admin-manual">
      <h1 className="font-display text-3xl font-extrabold tracking-tight text-slate-900">User manual</h1>
      <p className="text-sm text-slate-500">A PDF with a chapter for every feature and real screenshots. Only super admins can download it.</p>
      <div className="mt-6 grid gap-4 md:grid-cols-2">
        <div className="rounded-2xl border border-slate-200 bg-white p-6">
          <div className="eyebrow">Current edition</div>
          {st?.manual ? (
            <div className="mt-2 space-y-1 text-sm text-slate-700" data-testid="admin-manual-meta">
              <div>Generated <b>{fmtDT(st.manual.generated_at)}</b></div>
              <div><b data-testid="admin-manual-pages">{st.manual.pages}</b> pages · {st.manual.chapters} chapters · {st.manual.screenshots.length} screenshots</div>
            </div>
          ) : <div className="mt-2 text-sm text-slate-400">Not generated yet.</div>}
          <button onClick={downloadManual} data-testid="admin-manual-download-tab" className="mt-5 flex h-11 items-center gap-2 rounded-xl bg-slate-900 px-4 text-sm font-semibold text-white"><FileDown className="h-4 w-4" /> Download PDF</button>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-6">
          <div className="eyebrow">Regenerate</div>
          <p className="mt-2 text-sm text-slate-600">Takes fresh Playwright screenshots of the live app with the seeded demo accounts, then rebuilds the PDF and the How-it-works images. It makes a few demo sales on those accounts.</p>
          <div className="mt-3 font-mono text-xs text-slate-500" data-testid="admin-manual-job-state">Status: {st?.job?.state || "idle"}{st?.job?.finished_at ? ` · finished ${fmtDT(st.job.finished_at)}` : ""}</div>
          <div className={`mt-1 font-mono text-xs ${bstate === "failed" ? "text-rose-600" : bstate === "ready" ? "text-emerald-700" : "text-amber-700"}`} data-testid="admin-manual-browser-state">
            Screenshot browser: {{ checking: "checking…", installing: "installing (first run, a few minutes)…", ready: "ready", failed: "install failed" }[bstate] || "unknown"}
          </div>
          {bstate === "failed" && st?.browser?.error && <pre className="mt-2 max-h-32 overflow-auto rounded-lg bg-rose-50 p-2 text-[11px] text-rose-700" data-testid="admin-manual-browser-error">{st.browser.error}</pre>}
          <button onClick={regenerate} disabled={running || preparing} data-testid="admin-manual-regenerate" className="mt-4 flex h-11 items-center gap-2 rounded-xl border-2 border-slate-900 px-4 text-sm font-semibold text-slate-900 disabled:opacity-50">
            {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} {running ? "Regenerating…" : "Regenerate manual"}
          </button>
          {st?.job?.state === "failed" && <pre className="mt-3 max-h-40 overflow-auto rounded-lg bg-rose-50 p-2 text-[11px] text-rose-700">{st.job.log_tail}</pre>}
        </div>
      </div>
    </div>
  );
}
